import {
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
  type ChangeEvent,
  type KeyboardEvent,
} from "react"

import type { AcceptJsCardData } from "@src/entities/types/authorize-net"

export interface AuthorizeNetCardFormRef {
  /** Get current card data from the form */
  getCardData: () => AcceptJsCardData
  /** Validate form and return true if valid */
  validate: () => boolean
  /** Clear all form fields */
  clear: () => void
}

export interface AuthorizeNetCardFormProps {
  /** Called when form validation state changes */
  onValidationChange?: (isValid: boolean) => void
  /** Whether the form is disabled (e.g., during processing) */
  disabled?: boolean
  /** Error message to display */
  errorMessage?: string | null
  /** Gateway error code for support reference (e.g., "E00039") */
  errorCode?: string | null
}

/** Format card number with spaces every 4 digits */
function formatCardNumber(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 16)
  const groups = digits.match(/.{1,4}/g) || []
  return groups.join(" ")
}

/** Detect card type from number prefix */
function getCardType(cardNumber: string): string | null {
  const digits = cardNumber.replace(/\D/g, "")
  if (digits.startsWith("4")) return "visa"
  if (/^5[1-5]/.test(digits) || /^2[2-7]/.test(digits)) return "mastercard"
  if (/^3[47]/.test(digits)) return "amex"
  if (/^6(?:011|5)/.test(digits)) return "discover"
  return null
}

/** Validate card number using Luhn algorithm */
function isValidCardNumber(cardNumber: string): boolean {
  const digits = cardNumber.replace(/\D/g, "")
  if (digits.length < 13 || digits.length > 19) return false

  let sum = 0
  let isEven = false

  for (let i = digits.length - 1; i >= 0; i--) {
    let digit = parseInt(digits[i], 10)

    if (isEven) {
      digit *= 2
      if (digit > 9) digit -= 9
    }

    sum += digit
    isEven = !isEven
  }

  return sum % 10 === 0
}

/** Generate month options for select */
const MONTHS = Array.from({ length: 12 }, (_, i) => {
  const month = (i + 1).toString().padStart(2, "0")
  return { value: month, label: month }
})

/** Generate year options (current year + 15 years) */
function getYearOptions(): { value: string; label: string }[] {
  const currentYear = new Date().getFullYear()
  return Array.from({ length: 16 }, (_, i) => {
    const year = (currentYear + i).toString()
    return { value: year, label: year }
  })
}

/**
 * Reusable card input form for Authorize.net Accept.js.
 *
 * Features:
 * - Card number formatting with spaces
 * - Card type detection (Visa, MC, Amex, Discover)
 * - Client-side validation (Luhn algorithm, expiration, CVV length)
 * - Accessible form controls
 * - Imperative handle for parent component access
 *
 * @example
 * ```tsx
 * const formRef = useRef<AuthorizeNetCardFormRef>(null)
 *
 * const handleSubmit = () => {
 *   if (formRef.current?.validate()) {
 *     const cardData = formRef.current.getCardData()
 *     // Use cardData with tokenize()
 *   }
 * }
 *
 * return <AuthorizeNetCardForm ref={formRef} />
 * ```
 */
const AuthorizeNetCardForm = forwardRef<
  AuthorizeNetCardFormRef,
  AuthorizeNetCardFormProps
>(function AuthorizeNetCardForm(
  { onValidationChange, disabled = false, errorMessage, errorCode },
  ref
) {
  const [cardNumber, setCardNumber] = useState("")
  const [expirationMonth, setExpirationMonth] = useState("")
  const [expirationYear, setExpirationYear] = useState("")
  const [cvv, setCvv] = useState("")
  const [touched, setTouched] = useState({
    cardNumber: false,
    expiration: false,
    cvv: false,
  })

  const cardType = getCardType(cardNumber)
  const cvvLength = cardType === "amex" ? 4 : 3

  // Validation
  const isCardNumberValid = isValidCardNumber(cardNumber)
  const isExpirationValid = Boolean(expirationMonth && expirationYear)
  const isCvvValid = cvv.length === cvvLength
  const isFormValid = isCardNumberValid && isExpirationValid && isCvvValid

  // Notify parent of validation changes
  const checkValidation = useCallback(() => {
    const cardDigits = cardNumber.replace(/\D/g, "")
    const currentCvvLength = cardType === "amex" ? 4 : 3
    const valid =
      isValidCardNumber(cardNumber) &&
      Boolean(expirationMonth && expirationYear) &&
      cvv.length === currentCvvLength

    onValidationChange?.(valid)
  }, [cardNumber, expirationMonth, expirationYear, cvv, cardType, onValidationChange])

  // Handle card number input
  const handleCardNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCardNumber(e.target.value)
    setCardNumber(formatted)
    setTimeout(checkValidation, 0)
  }

  // Handle CVV input (restrict to digits and proper length)
  const handleCvvChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, cvvLength)
    setCvv(value)
    setTimeout(checkValidation, 0)
  }

  // Handle month change
  const handleMonthChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setExpirationMonth(e.target.value)
    setTimeout(checkValidation, 0)
  }

  // Handle year change
  const handleYearChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setExpirationYear(e.target.value)
    setTimeout(checkValidation, 0)
  }

  // Expose imperative handle
  useImperativeHandle(ref, () => ({
    getCardData: (): AcceptJsCardData => ({
      cardNumber: cardNumber.replace(/\s/g, ""),
      month: expirationMonth,
      year: expirationYear,
      cardCode: cvv,
    }),
    validate: (): boolean => {
      setTouched({ cardNumber: true, expiration: true, cvv: true })
      return isFormValid
    },
    clear: () => {
      setCardNumber("")
      setExpirationMonth("")
      setExpirationYear("")
      setCvv("")
      setTouched({ cardNumber: false, expiration: false, cvv: false })
    },
  }))

  const yearOptions = getYearOptions()

  return (
    <div className="authorize-net-card-form">
      {/* Error Message */}
      {errorMessage && (
        <div className="bg-red/20 border border-red rounded-sm mb-4 p-3 text-sm text-red">
          {errorMessage}
          {errorCode && (
            <span className="block mt-1 font-normal opacity-70">
              Error code: {errorCode}
            </span>
          )}
        </div>
      )}

      {/* Card Number */}
      <div className="mb-4">
        <label
          htmlFor="card-number"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Card Number
        </label>
        <div className="relative">
          <input
            id="card-number"
            type="text"
            inputMode="numeric"
            autoComplete="cc-number"
            placeholder="1234 5678 9012 3456"
            value={cardNumber}
            onChange={handleCardNumberChange}
            onBlur={() => setTouched((t) => ({ ...t, cardNumber: true }))}
            disabled={disabled}
            className={`w-full px-3 py-2 border rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
              touched.cardNumber && !isCardNumberValid
                ? "border-red"
                : "border-gray-300"
            } ${disabled ? "bg-gray-100 cursor-not-allowed" : ""}`}
            aria-invalid={touched.cardNumber && !isCardNumberValid}
            aria-describedby={
              touched.cardNumber && !isCardNumberValid
                ? "card-number-error"
                : undefined
            }
          />
          {cardType && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-gray-500 uppercase">
              {cardType}
            </span>
          )}
        </div>
        {touched.cardNumber && !isCardNumberValid && (
          <p id="card-number-error" className="mt-1 text-sm text-red">
            Please enter a valid card number
          </p>
        )}
      </div>

      {/* Expiration & CVV Row */}
      <div className="flex gap-4 mb-4">
        {/* Expiration Date */}
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Expiration
          </label>
          <div className="flex gap-2">
            <select
              value={expirationMonth}
              onChange={handleMonthChange}
              onBlur={() => setTouched((t) => ({ ...t, expiration: true }))}
              disabled={disabled}
              className={`flex-1 px-3 py-2 border rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                touched.expiration && !expirationMonth
                  ? "border-red"
                  : "border-gray-300"
              } ${disabled ? "bg-gray-100 cursor-not-allowed" : ""}`}
              aria-label="Expiration month"
            >
              <option value="">MM</option>
              {MONTHS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
            <select
              value={expirationYear}
              onChange={handleYearChange}
              onBlur={() => setTouched((t) => ({ ...t, expiration: true }))}
              disabled={disabled}
              className={`flex-1 px-3 py-2 border rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                touched.expiration && !expirationYear
                  ? "border-red"
                  : "border-gray-300"
              } ${disabled ? "bg-gray-100 cursor-not-allowed" : ""}`}
              aria-label="Expiration year"
            >
              <option value="">YYYY</option>
              {yearOptions.map((y) => (
                <option key={y.value} value={y.value}>
                  {y.label}
                </option>
              ))}
            </select>
          </div>
          {touched.expiration && !isExpirationValid && (
            <p className="mt-1 text-sm text-red">
              Please select expiration date
            </p>
          )}
        </div>

        {/* CVV */}
        <div className="w-24">
          <label
            htmlFor="cvv"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            CVV
          </label>
          <input
            id="cvv"
            type="text"
            inputMode="numeric"
            autoComplete="cc-csc"
            placeholder={cardType === "amex" ? "1234" : "123"}
            value={cvv}
            onChange={handleCvvChange}
            onBlur={() => setTouched((t) => ({ ...t, cvv: true }))}
            disabled={disabled}
            maxLength={cvvLength}
            className={`w-full px-3 py-2 border rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
              touched.cvv && !isCvvValid ? "border-red" : "border-gray-300"
            } ${disabled ? "bg-gray-100 cursor-not-allowed" : ""}`}
            aria-invalid={touched.cvv && !isCvvValid}
            aria-describedby={
              touched.cvv && !isCvvValid ? "cvv-error" : undefined
            }
          />
          {touched.cvv && !isCvvValid && (
            <p id="cvv-error" className="mt-1 text-sm text-red">
              {cvvLength} digits required
            </p>
          )}
        </div>
      </div>
    </div>
  )
})

export default AuthorizeNetCardForm

